import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IAutoEntityService, IEntityInfo, IEntityWithPageInfo, Page } from '@briebug/ngrx-auto-entity';
import { Criteria } from '../utils/criteria.model';
import { SearchCriteriaGroup } from '../utils/searchCriteriaGroup';
import { AppConfigService } from './app-config.service';

export abstract class EntityService<T> implements IAutoEntityService<any> {

    constructor(private http: HttpClient, private configService: AppConfigService) { }

    load(entityInfo: IEntityInfo, id: any): Observable<any> {
        return this.http.get<any>(
            `${this.configService.config.apiUrl}/${this.getEntityUri(entityInfo)}/${id}`
        );
    }

    loadAll(entityInfo: IEntityInfo, criteria?: Criteria): Observable<any[]> {
        if (criteria) {
            let params: HttpParams = new HttpParams();
            if (criteria && criteria.sort) {
                params = params.set("sort", criteria.sort.field)
                    .set("dir", criteria.sort.dir);
            }
            return this.http.post<any[]>(`${this.configService.config.apiUrl}/${this.getEntityUri(entityInfo)}/list`, criteria.filter, { params: params });
        } else {
            return this.http.post<any[]>(`${this.configService.config.apiUrl}/${this.getEntityUri(entityInfo)}/list`, null);
        }
    }

    create(entityInfo: IEntityInfo, entity: any): Observable<any> {
        return this.http.post<any>(
            `${this.configService.config.apiUrl}/${this.getEntityUri(entityInfo)}`,
            entity
        );
    }

    createMany(entityInfo: IEntityInfo, entities: any[]): Observable<any[]> {
        return this.http.post<any[]>(
            `${this.configService.config.apiUrl}/${this.getEntityUri(entityInfo)}/createMany`,
            entities
        );
    }


    update(entityInfo: IEntityInfo, entity: any): Observable<any> {
        return this.http.put<any>(
            `${this.configService.config.apiUrl}/${this.getEntityUri(entityInfo)}`,
            entity
        );
    }

    updateMany(entityInfo: IEntityInfo, entities: any[]): Observable<any[]> {
        return this.http.put<any[]>(
            `${this.configService.config.apiUrl}/${this.getEntityUri(entityInfo)}/updateMany`,
            entities
        );
    }

    delete(entityInfo: IEntityInfo, entity: any): Observable<any> {
        return this.http.delete<any>(
            `${this.configService.config.apiUrl}/${this.getEntityUri(entityInfo)}/${entity.id}`
        ).pipe(map(() => entity));
    }

    deleteMany(entityInfo: IEntityInfo, entities: any[]): Observable<any[]> {
        return this.http.post<any[]>(
            `${this.configService.config.apiUrl}/${this.getEntityUri(entityInfo)}/deleteMany`,
            entities
        ).pipe(map(() => entities));
    }

    loadPage(entityInfo: IEntityInfo, page: Page, criteria?: Criteria): Observable<IEntityWithPageInfo<any>> {
        let params = new HttpParams()
            .set("page", page.page.toString())
            .set("size", page.size.toString());

        if (criteria) {
            if (criteria.sort) {
                params = params.set("sort", criteria.sort.field)
                    .set("dir", criteria.sort.dir);
            }
        }
        return this.http.post<any>(`${this.configService.config.apiUrl}/${this.getEntityUri(entityInfo)}/page`, criteria?.filter, { params: params }).pipe(
            map(response => ({
                pageInfo: {
                    page: { page: page.page, size: page.size },
                    totalCount: response.totalElements
                },
                entities: response.content
            }))
        );
    }

    exportExcel(uriName: string, criteria?: Criteria) {
        let params: HttpParams = new HttpParams();
        let body: SearchCriteriaGroup[] = null;
        if (criteria) {
            if (criteria.sort) {
                params = params.set('sort', criteria.sort.field)
                    .set('dir', criteria.sort.dir);
            }
            if (criteria.filter) {
                body = criteria.filter;
            }
        }
        this.http.post(`${this.configService.config.apiUrl}/${uriName}/export-excel`, body, { observe: 'response', responseType: 'blob', params: params })
            .subscribe(
                response => {
                    let url = window.URL.createObjectURL(response.body);
                    let a = document.createElement('a');
                    document.body.appendChild(a);
                    a.href = url;
                    a.download = uriName;
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();
                });
    }

    getEntityUri(entityInfo: IEntityInfo): string {
        return entityInfo.uriName ? entityInfo.uriName : entityInfo.modelName.toLowerCase() + 's';
    }
}